import React from 'react'
import { VideoAnnotationReadInstance } from 'api/schema'
import styles from './ReportPreview.module.css'
import { IReportFilters } from 'utils/interfaces'
import { formatDate } from 'utils/dateUtils'
import getUserName from 'utils/getUserName'
import { groupBy } from 'lodash'
import clsx from 'clsx'
import { isOverdue } from 'utils/isTaskOverdue'
import CarouselImages from './CarouselImages/CarouselImages'
import { branding } from 'utils/branding'
import { useTranslation } from 'react-i18next'

const ReportPreviewLayout = ({
  filters,
  tasks,
  author,
  projectName,
  selectedPhotos,
  onSelectedPhotosChange,
}: {
  filters: IReportFilters
  tasks: VideoAnnotationReadInstance[]
  author: string
  projectName: string
  selectedPhotos: Record<number, number>
  onSelectedPhotosChange?: (selectedPhotosNew: Record<number, number>) => void
}) => {
  const { t } = useTranslation()
  const formatDateStandart = (date: Date) => {
    return formatDate(date, 'dd.MM.yyyy')
  }
  const tasksDividedByExecutor = groupBy(tasks, obj => (obj.assignee_to_id ? obj.assignee_to_id : 'undefined'))
  const formatDateLocal = (date: string) => {
    return formatDate(new Date(date), 'dd.MM.yyyy')
  }
  const getImageSrc = (task: VideoAnnotationReadInstance) => {
    return task.frames[task.frames.length - 1]?.frame?.thumbnail
  }
  const onCurrentPhotoIndexChange = (taskId: number, photoIndex: number) => {
    if (onSelectedPhotosChange) {
      const newSelectedPhotos = { ...selectedPhotos }
      newSelectedPhotos[taskId] = photoIndex
      onSelectedPhotosChange(newSelectedPhotos)
    }
  }

  return (
    <div className={styles.root}>
      <img className={styles.logoImg} src={branding.getLightLogo()} alt="logo" />
      <div className={styles.subtitle}>{t('report_preview.service_description')}</div>
      <div className={styles.subtitle2}>{t('report_preview.print_version')}</div>
      <div className={styles.title}>{branding.getReportTitle(t)}</div>
      <div className={styles.title}>{projectName}</div>

      <div className={styles.createdDate}>
        {(filters.dateStart || filters.dateEnd) && (
          <div className={styles.period}>
            {t('report_preview.task_creation_dates')}
            {filters.dateStart && <span>{' ' + formatDateStandart(filters.dateStart)}</span>}
            {filters.dateStart && filters.dateEnd && <span> - </span>}
            {filters.dateEnd && <span>{formatDateStandart(filters.dateEnd)} </span>}
          </div>
        )}
        <div className={styles.period}>
          {t('report_preview.report_creation_date')} {formatDateStandart(new Date())}
        </div>
      </div>
      {tasks.length === 0 && <div className={styles.noTasks}>{t('report_preview.no_tasks')}</div>}
      {Object.keys(tasksDividedByExecutor).map((executorId, index) => {
        const tasksForExecutor = tasksDividedByExecutor[executorId]
        const executorName = tasksForExecutor[0].assignee_to
          ? getUserName(tasksForExecutor[0].assignee_to)
          : 'Без исполнителя'
        return (
          <div key={executorId || 999} className={styles.taskExecutor}>
            <div className={styles.taskGroupTitle}>
              {index + 1}. {executorName}:
            </div>
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr className={clsx(styles.tableTitle)}>
                  <th className={clsx(styles.tableCell, styles.col1)}>{t('report_preview.number')}</th>
                  <th className={clsx(styles.tableCell, styles.col2)}>{t('report_preview.video')}</th>
                  <th className={clsx(styles.tableCell, styles.col3)}>{t('report_preview.description')}</th>
                  <th className={clsx(styles.tableCell, styles.col3)}>{t('report_preview.milestone')}</th>
                  <th className={clsx(styles.tableCell, styles.col4)}>{t('report_preview.deadline')}</th>
                  <th className={clsx(styles.tableCell, styles.col5)}>{t('report_preview.status')}</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {tasksForExecutor.map((task: VideoAnnotationReadInstance, index2) => (
                  <tr key={index2 + '.' + task.id}>
                    <td className={clsx(styles.tableCell, styles.col1)}>
                      {index + 1}.{index2 + 1}
                    </td>
                    <td className={clsx(styles.tableCell, styles.col2)}>
                      {getImageSrc(task) ? (
                        <img src={getImageSrc(task) || ''} alt="Video preview" width="200" height="150" />
                      ) : (
                        t('report_preview.no_image')
                      )}
                    </td>
                    <td className={clsx(styles.tableCell, styles.col3)}>{task.text}</td>
                    <td className={clsx(styles.tableCell, styles.col3)}>
                      {!!task.frames.length && task.frames[0].frame.mark_number
                        ? t('report_preview.milestone') + task.frames[0].frame.mark_number
                        : '-'}
                    </td>
                    <td className={clsx(styles.tableCell, styles.col4)}>
                      {task.scheduled_to_resolve_at && <span>{formatDateLocal(task.scheduled_to_resolve_at)}</span>}
                      {!task.scheduled_to_resolve_at && <span>{t('report_preview.no_deadline')}</span>}
                      {isOverdue(task) && <div> ({t('report_preview.overdue')})</div>}
                    </td>
                    <td className={clsx(styles.tableCell, styles.col5)}>
                      {!task.is_resolved && t('report_preview.active')}
                      {task.is_resolved && t('report_preview.completed')}
                      {task.is_resolved && task.resolved_at && <div>({formatDateLocal(task.resolved_at)})</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
      {Object.keys(tasksDividedByExecutor).map((executorId, executorIndex) => {
        const tasksForExecutor = tasksDividedByExecutor[executorId]
        const executorName = tasksForExecutor[0].assignee_to
          ? getUserName(tasksForExecutor[0].assignee_to)
          : t('report_preview.no_executor')
        return (
          <React.Fragment key={executorId}>
            {tasksForExecutor.map((task, taskIndex) => {
              return (
                <div key={executorId + '.' + task.id + taskIndex} className={styles.taskExecutor}>
                  <CarouselImages
                    imagesUrls={task.frames.map(frame => frame.image)}
                    dateTimes={task.frames.map(frame => frame.video.date)}
                    currentPhotoIndex={selectedPhotos[taskIndex] || 0}
                    onCurrentPhotoIndexChange={(photoIndex: number) => onCurrentPhotoIndexChange(task.id, photoIndex)}
                  />
                  <table className={clsx(styles.table, styles.tableSmall)}>
                    <thead className={styles.tableHeader}>
                      <tr className={clsx(styles.tableRow, styles.taskRow)}>
                        <th className={styles.tableCellRegular}>{t('report_preview.number')}</th>
                        <th className={styles.tableCellRegular}>{t('report_preview.executor')}</th>
                        <th className={styles.tableCellRegular}>{t('report_preview.creation_date')}</th>
                        <th className={styles.tableCellRegular}>{t('report_preview.description')}</th>
                        <th className={styles.tableCellRegular}>{t('report_preview.milestone')}</th>
                        <th className={styles.tableCellRegular}>{t('report_preview.deadline')}</th>
                        <th className={styles.tableCellRegular}>{t('report_preview.status')}</th>
                      </tr>
                    </thead>
                    <tbody className={styles.tableBody}>
                      <tr key={task.id} className={clsx(styles.tableRow, styles.taskRow)}>
                        <td className={styles.tableCell}>
                          {executorIndex + 1}.{taskIndex + 1}
                        </td>
                        <td className={styles.tableCell}>{executorName}</td>
                        <td className={styles.tableCell}>{formatDateLocal(task.created_at)}</td>
                        <td className={styles.tableCell}>{task.text}</td>
                        <td className={styles.tableCell}>
                          {!!task.frames.length && task.frames[0].frame.mark_number
                            ? t('report_preview.milestone') + task.frames[0].frame.mark_number
                            : '-'}
                        </td>
                        <td className={styles.tableCell}>
                          {task.scheduled_to_resolve_at && <span>{formatDateLocal(task.scheduled_to_resolve_at)}</span>}
                          {!task.scheduled_to_resolve_at && <span>{t('report_preview.no_deadline')}</span>}
                          {isOverdue(task) && <div> ({t('report_preview.overdue')})</div>}
                        </td>
                        <td className={styles.tableCell}>
                          {!task.is_resolved && t('report_preview.active')}
                          {task.is_resolved && t('report_preview.completed')}
                          {task.is_resolved && task.resolved_at && <div>({formatDateLocal(task.resolved_at)})</div>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            })}
          </React.Fragment>
        )
      })}
      <div className={styles.divFooter}>
        <div className={styles.divHeader} />
        <div>
          {t('report_preview.task_creation_dates')} {filters.dateStart && formatDateLocal(filters.dateStart.toString())}{' '}
          - {filters.dateEnd && formatDateLocal(filters.dateEnd.toString())}
        </div>
        <div>
          {t('report_preview.report_generated')} <span className={styles.link}>{branding.getServiceName()}</span>
        </div>
        <div>
          {t('report_preview.report_creation_date')} {formatDateLocal(new Date().toString())}
        </div>
      </div>
    </div>
  )
}

export default ReportPreviewLayout
